import { createContext, useContext, useEffect, useMemo, useState } from 'react';

/**
 * Lets a feature put its own full-height aside into the shell, between the
 * sidebar and the main column.
 *
 * Affiliate needs this: `Aside - Affiliate programme` (1:16694) sits flush
 * against the 76px rail at y=0 and pushes the top bar to start after it, so it
 * can't live inside the outlet — the outlet begins below the top bar and inside
 * the shell's own padding.
 *
 * Same shape as TopbarSlot, one level out: the page publishes, the shell reads.
 */
const ShellAsideContext = createContext({
  node: null,
  setNode: () => {},
  isAsideCollapsed: false,
  toggleAside: () => {},
  sidebarCollapsed: false,
  toggleSidebar: () => {},
});

export function ShellAsideProvider({
  children,
  isAsideCollapsed = false,
  toggleAside = () => {},
  sidebarCollapsed = false,
  toggleSidebar = () => {},
}) {
  const [node, setNode] = useState(null);
  const value = useMemo(
    () => ({
      node,
      setNode,
      isAsideCollapsed,
      toggleAside,
      sidebarCollapsed,
      toggleSidebar,
    }),
    [node, isAsideCollapsed, toggleAside, sidebarCollapsed, toggleSidebar],
  );
  return <ShellAsideContext.Provider value={value}>{children}</ShellAsideContext.Provider>;
}

/** Read the current aside — used by AppLayout. */
export function useShellAsideNode() {
  return useContext(ShellAsideContext).node;
}

/** Read the aside collapse controls — used by aside components. */
export function useShellAsideControl() {
  return useContext(ShellAsideContext);
}

/**
 * Publish `render()` as the shell aside for as long as this route is mounted.
 * `deps` behaves like useEffect's, for the same reason TopbarSlot's does.
 */
export function useShellAside(render, deps = []) {
  const { setNode } = useContext(ShellAsideContext);

  useEffect(() => {
    setNode(render());
    return () => setNode(null);
  }, deps);
}
